/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import { useEffect, useContext } from 'react';
import { AppContext } from '../../../../Store';
import { EmailFieldContext } from '../../../../state-management/contexts/regform-fields/email-field-context';
import { PasswordFieldContext } from '../../../../state-management/contexts/regform-fields/password-field-context';
import { NewPasswordFieldContext } from '../../../../state-management/contexts/regform-fields/new-password-field-context';
import { ConfirmPasswordFieldContext } from '../../../../state-management/contexts/regform-fields/confirm-password-field-context';
import { getLoginHubBridge } from '../../../../helpers/login-hub-bridge';
import { getAuthResultFromUrl } from '../../../../helpers/oauth/auth-result';

type CallbackFunction = () => void;
export interface LoginHubProps {
	onEmailChangeCallback: (
		emailValue?: string,
		showError?: boolean,
		callback?: Function
	) => void;
	onCurrentPasswordChangeCallback: (
		passwordValue?: string,
		showError?: boolean,
		callback?: Function
	) => void;
	onNewPasswordChangeCallback: (
		passwordValue?: string,
		showError?: boolean,
		callback?: Function
	) => void;
	onConfirmNewPasswordChangeCallback: (
		passwordValue?: string,
		showError?: boolean,
		callback?: Function
	) => void;
	onTermsCheckedCallback?: CallbackFunction;
	handleSubmit: React.FormEventHandler<HTMLFormElement>;
}

interface WipAuth {
	playerId: number;
	resourceId: number;
}

interface ApiResponse {
	status: number;
	data?: unknown;
}

export class ApiService {
	private baseUrl: string = '';
	private sameOriginRequestPolicy: RequestInit = {
		mode: 'same-origin',
		credentials: 'same-origin',
		referrerPolicy: 'same-origin',
	};

	constructor(baseUrl?: string, requestPolicy?: RequestInit) {
		if (baseUrl) {
			this.baseUrl = baseUrl;
		}
		if (requestPolicy) {
			this.sameOriginRequestPolicy = requestPolicy;
		}
	}

	public async postData(playerId: number, resourceId: number): Promise<ApiResponse> {
		const base: string = this.baseUrl || '';
		const dataToSend: WipAuth = { playerId: playerId, resourceId: resourceId };
		try {
			const response: Response = await fetch(`${base}/api/wip`, {
				...this.sameOriginRequestPolicy,
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(dataToSend),
			});

			try {
				const jsonResponse = await response.json();
				return jsonResponse;
			} catch (jsonError) {
				return {
					status: response.status
				};
			}
		} catch (e: unknown) {
			throw new Error('unexpected');
		}
	}

}

export function getClientVersion(): string | undefined {
	const searchParams = new URLSearchParams(window.location.search);
	const version = searchParams.get('client');
	if (!version) {
		return undefined;
	}
	return version;
}

/**
 * Use the platform parameter send by the loginhub client implementation to determine platform
 */
export function isWebPlatform(): boolean {
	return new URLSearchParams(window.location.search).get('platform') === 'web';
}

/**
 * Compares the client version reported via the `client` query parameter against
 * a minimum version (e.g. "4.4.3"), component by component.
 * Returns false when no client version is available (assumed to be an old client).
 */
export function isClientVersionAtLeast(minVersion: string): boolean {
	const current = getClientVersion();
	if (!current) {
		return false;
	}

	// A client that reports "Unknown" is treated as new enough.
	if (current.toLowerCase() === 'unknown') {
		return true;
	}

	const toParts = (version: string): number[] => version.split('.').map((part) => parseInt(part, 10) || 0);
	const currentParts = toParts(current);
	const minParts = toParts(minVersion);

	for (let i = 0; i < Math.max(currentParts.length, minParts.length); i++) {
		const currentPart = currentParts[i] ?? 0;
		const minPart = minParts[i] ?? 0;
		if (currentPart !== minPart) {
			return currentPart > minPart;
		}
	}

	return true; // versions are equal
}

/*! @preserve LoginHubClientBridge communication */
export const closeWebview = (
	eventOrPlayerId?: React.MouseEvent<HTMLButtonElement> | number,
	lpsToken?: string,
	lpsLoginUrl?: string,
) => {
	let playerId: number | undefined;

	// Check if the passed argument is an event (e.g., from onClick={closeWebview})
	if (typeof eventOrPlayerId === 'object' && 'preventDefault' in eventOrPlayerId) {
		eventOrPlayerId.preventDefault();
	} else if (typeof eventOrPlayerId === 'number') {
		playerId = eventOrPlayerId;
	}

	const bridge = getLoginHubBridge();

	// The `action` field is only understood by client version 4.4.3 and newer.
	// For older clients we keep the previous behaviour (no action in the payload).
	if (isClientVersionAtLeast('4.4.3')) {
		let resolvedAction = 'close';
		if (lpsLoginUrl) {
			resolvedAction = 'lps_login';
		} else if (lpsToken) {
			resolvedAction = 'game_login';
		}

		const payload = JSON.stringify({ playerId, lpsToken, loginUrl: lpsLoginUrl, action: resolvedAction });
		bridge.close(payload);
		return;
	}

	// Legacy behaviour for clients older than 4.4.3.
	// Passing the payload will trigger a game reload with login of the new player
	if ((playerId !== undefined && lpsToken !== undefined) || lpsLoginUrl !== undefined) {
		const payload = JSON.stringify({ playerId, lpsToken, loginUrl: lpsLoginUrl });
		bridge.close(payload);
	} else {
		bridge.close();
	}
};

/**
 * Navigate to a login-hub screen via the URL hash.
 *
 * Removes the `auth_result` query parameter so a previous OAuth result does not
 * leak into the screen being navigated to. Setting the hash afterwards triggers
 * the `hashchange` event the router listens for.
 *
 * @param path Screen path without the leading `#` (e.g. `/changeEmailAndPassword`)
 */
export const navigateTo = (path: string) => {
	const url = new URL(window.location.href);
	if (url.searchParams.has('auth_result')) {
		url.searchParams.delete('auth_result');
		window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
	}
	window.location.hash = `#${path}`;
};

/**
 * Custom hook to reset error states on component mount for login-hub forms.
 * This prevents error messages from persisting when navigating between different login-hub screens.
 *
 * @param options Configuration object to specify which field errors to reset
 * @param options.resetEmail - Reset email field errors (default: false)
 * @param options.resetPassword - Reset password field errors (default: false)
 * @param options.resetNewPassword - Reset new password field errors (default: false)
 * @param options.resetConfirmPassword - Reset confirm password field errors (default: false)
 */
export const useResetLoginHubErrors = (options: {
	resetEmail?: boolean;
	resetPassword?: boolean;
	resetNewPassword?: boolean;
	resetConfirmPassword?: boolean;
} = {}) => {
	const [, stateDispatch] = useContext(AppContext);
	const [, emailDispatch] = useContext(EmailFieldContext);
	const [, passwordDispatch] = useContext(PasswordFieldContext);
	const [, newPasswordDispatch] = useContext(NewPasswordFieldContext);
	const [, confirmPasswordDispatch] = useContext(ConfirmPasswordFieldContext);

	useEffect(() => {
		if (!getAuthResultFromUrl()) {
			stateDispatch({ type: 'HIDE_SERVER_ERROR' });
			stateDispatch({ type: 'HIDE_LOGIN_HUB_FORM_SUCCESS' });
		}

		if (options.resetEmail) {
			emailDispatch({ type: 'HIDE_EMAIL_ERROR' });
			emailDispatch({ type: 'REMOVE_EMAIL_ERROR' });
		}

		if (options.resetPassword) {
			passwordDispatch({ type: 'HIDE_PASSWORD_ERROR' });
			passwordDispatch({ type: 'REMOVE_PASSWORD_ERROR' });
		}

		if (options.resetNewPassword) {
			newPasswordDispatch({ type: 'HIDE_PASSWORD_ERROR' });
			newPasswordDispatch({ type: 'REMOVE_PASSWORD_ERROR' });
		}

		if (options.resetConfirmPassword) {
			confirmPasswordDispatch({ type: 'HIDE_PASSWORD_ERROR' });
			confirmPasswordDispatch({ type: 'REMOVE_PASSWORD_ERROR' });
		}
	}, [
		stateDispatch,
		emailDispatch,
		passwordDispatch,
		newPasswordDispatch,
		confirmPasswordDispatch,
		options.resetEmail,
		options.resetPassword,
		options.resetNewPassword,
		options.resetConfirmPassword,
	]);
};
